<template>
	<section>
		<ButtonBack />
		<el-row>
			<h1>Werberatsentscheidungen</h1>
		</el-row>

		<h2>Filter</h2>
		<TheForm :model="request" :rules="rules" :id="id" ref="formRef" :persistForm="true" formNamespace="entscheidungenFilter" :autoSave="true">
			<el-row>
				<el-form-item label="Freitextsuche" prop="searchTerm" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-input v-model="request.searchTerm" placeholder="Suche" clearable />
				</el-form-item>

				<el-col :sm="12" :xs="24">
					<el-row>
						<el-form-item label="Begründung" prop="hasReason" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
							<el-select v-model="request.hasReason" placeholder="Alle">
								<el-option v-for="item in reasonOptions" :key="item.value" :label="item.label" :value="item" />
							</el-select>
						</el-form-item>
						<el-form-item label="Freigabe Begründung" prop="isShared" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
							<el-select v-model="request.isShared" placeholder="Alle">
								<el-option v-for="item in isSharedOptions" :key="item.value" :label="item.label" :value="item" />
							</el-select>
						</el-form-item>
					</el-row>
				</el-col>
			</el-row>

			<el-row>
				<el-form-item label="Entscheidung" prop="state" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-select v-model="request.state" placeholder="Alle">
						<el-option v-for="item in decisionStateOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-form-item>

				<el-form-item label="Gruppe" prop="role" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-select v-model="request.role" placeholder="Alle">
						<el-option v-for="item in userRoleOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-form-item>
			</el-row>

			<el-row>
				<el-form-item label="Zeitraum (Erstellung)" prop="timePeriod" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-checkbox-group v-model="selectedTimePeriod" placeholder="Alle">
						<el-checkbox
							v-for="item in timePeriodOptions"
							:key="item.value"
							:value="item.value"
							:border="true"
							@change="() => handleClickOptionWrapper(item)"
						>
							{{ item.label }}
						</el-checkbox>
					</el-checkbox-group>
				</el-form-item>
				<el-col :sm="4" :xs="24"></el-col>
				<el-form-item label="" prop="dateRange" class="el-col el-col-24 el-col-sm-8 el-col-xs-24">
					<div class="datepicker-range__labels">
						<label class="el-form-item__label">Von</label>
						<label class="el-form-item__label">Bis</label>
					</div>
					<el-date-picker
						v-model="dateRange"
						type="daterange"
						:format="'DD.MM.YYYY'"
						range-separator=""
						:clearable="true"
						start-placeholder="TT.MM.JJJJ"
						end-placeholder="TT.MM.JJJJ"
					/>
				</el-form-item>
			</el-row>
			<el-row class="button-panel">
				<TheButton
					type="danger"
					@buttonclick="
						tablePage = 0;
						doSearch();
					"
					isSubmit
					>Anwenden</TheButton
				>
				<TheButton type="default" @buttonclick="resetSearch">
					<el-icon><Close /></el-icon>
					Filter zurücksetzen
				</TheButton>
			</el-row>
		</TheForm>
	</section>

	<div class="bg--white">
		<section id="searchresults">
			<h2>Werberatsentscheidungen ({{ $getCountErgebnisseLabel(decisionsTotalCount) }})</h2>

			<DecisionsTable
				:loading="decisionsTableLoading"
				:tableData="filteredDecisions"
				:totalCount="decisionsTotalCount"
				@loadNextPage="loadNextPage"
			/>
		</section>
	</div>
</template>

<script lang="ts">
import DecisionsTable from '@/components/tables/DecisionsTable.vue';
import { searchDecisions } from '@/requests/searches';
import { DecisionResponse, SearchDecisionRequest } from '@/types/types';
import { resetForm, validateForm } from '@/utils/functions';
import { handleClickTimePeriodOptionCheckbox, handleSearchDateRangeChange, prepareSearchRequest } from '@/utils/search/searchUtils';
import { Close, Plus } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { returnFormOptions } from '../utils/formOptions.js';
import { texts } from '../utils/texts';

interface SearchDecisionsData {
	request: SearchDecisionRequest;
	rules: Object;
	id: string;
	dateRange: string[] | null;
	selectedTimePeriod: any[];
	tablePage: number;
	itemsPerPage: number;
	filteredDecisions: DecisionResponse[];
	decisionsTotalCount: number;
	decisionsTableLoading: boolean;
}

export default defineComponent({
	name: 'WerberatsentscheidungenView',
	data(): SearchDecisionsData {
		return {
			request: {
				searchTerm: '',
				state: null,
				role: null,
				isShared: null,
				hasReason: null,
				fromDate: null,
				toDate: null,
			},
			rules: {},
			id: 'searchForm',
			dateRange: null,
			selectedTimePeriod: [],
			tablePage: 0,
			itemsPerPage: 14,
			filteredDecisions: [],
			decisionsTotalCount: 0,
			decisionsTableLoading: false,
		};
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	components: {
		ButtonBack,
		TheButton,
		TheForm,
		Plus,
		Close,
		DecisionsTable,
	},
	computed: {
		isSharedOptions() {
			return returnFormOptions('isShared', false, true);
		},
		reasonOptions() {
			return returnFormOptions('reason', false, true);
		},
		decisionStateOptions() {
			return returnFormOptions('decisionStateOptions', false, true);
		},
		userRoleOptions() {
			return returnFormOptions('userRole', false, true);
		},
		timePeriodOptions() {
			return returnFormOptions('timePeriod');
		},
	},
	methods: {
		handleClickOptionWrapper(item) {
			const { newSelectedTimePeriod, newTimePeriodOptions, newDateRange } = handleClickTimePeriodOptionCheckbox(
				item,
				this.selectedTimePeriod,
				this.timePeriodOptions,
			);

			this.selectedTimePeriod = newSelectedTimePeriod;
			this.timePeriodOptions = newTimePeriodOptions;
			this.dateRange = newDateRange;
		},
		resetSearch() {
			resetForm(this.formRef, this.id);
			this.dateRange = null;
			this.tablePage = 0;
			this.doSearch();
		},
		async doSearch() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.decisionsTableLoading = true;
			const searchRequest = prepareSearchRequest(this.request);

			const searchResult = await searchDecisions(searchRequest, this.tablePage, this.itemsPerPage);
			if (searchResult.success) {
				this.decisionsTotalCount = searchResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredDecisions = searchResult.data.items;
				} else {
					this.filteredDecisions = [...this.filteredDecisions, ...searchResult.data.items];
				}
			} else {
				console.error('searchResult', searchResult.error);
				this.filteredDecisions = [];
				this.decisionsTotalCount = 0;
			}
			this.decisionsTableLoading = false;
		},
		loadNextPage() {
			this.tablePage++;
			this.doSearch();
		},
	},
	mounted() {
		if (this.$route.query.searchTerm) {
			this.request.searchTerm = String(this.$route.query.searchTerm);
		}

		this.doSearch();
	},
	watch: {
		dateRange: {
			handler(val) {
				if (val && val.length === 2) {
					const { fromDate, toDate } = handleSearchDateRangeChange(val);
					this.request.fromDate = fromDate;
					this.request.toDate = toDate;
				} else {
					this.request.fromDate = null;
					this.request.toDate = null;
					this.selectedTimePeriod = [];
					this.timePeriodOptions.forEach((option: any) => (option.checked = false));
				}
			},
			deep: true,
		},
		request: {
			handler() {
				if (this.request.fromDate && this.request.toDate) {
					this.dateRange = [this.request.fromDate, this.request.toDate];
				}
			},
			deep: true,
		},
	},
});
</script>
